import React, { useMemo, useState } from 'react';
import { ChevronRight, MapPin, Sparkles, X, ChevronDown, Plus, Search, Loader2, Crosshair } from 'lucide-react';
import { filterAndRankLocations, buildLocationFullPath } from '@/utils/locationSearch';
import { CommanServices } from '@/services/commanServices';

const PATH_SEP = ' → ';

const getLocName = (loc) => loc?.zone_name || loc?.auto_label || loc?.name || '';

const getIsAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const role = (user?.role || '').toLowerCase();
    return role === 'admin' || role === 'superadmin';
  } catch {
    return false;
  }
};

const buildFullPath = (loc, allLocations = []) => {
  const parts = [];
  if (loc?.ancestors?.length > 0) {
    loc.ancestors.forEach((a) => {
      const val = a?.value || a?.name;
      if (val) parts.push(val);
    });
  } else if (loc?.parent_id && allLocations.length > 0) {
    const byId = new Map(allLocations.map((l) => [l.id, l]));
    const chain = [];
    let current = loc;
    const visited = new Set();
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      chain.unshift(getLocName(current));
      const parent = byId.get(current.parent_id);
      if (!parent) break;
      current = parent;
    }
    return chain.filter(Boolean).join(PATH_SEP);
  }
  const selfName = getLocName(loc);
  if (selfName && (!parts.length || parts[parts.length - 1] !== selfName)) {
    parts.push(selfName);
  }
  return parts.filter(Boolean).join(PATH_SEP);
};

const sortChildrenByHierarchy = (nodes = []) => {
  return [...nodes].sort((a, b) => {
    const aKey = (a.auto_label || a.zone_name || a.name || '').toString();
    const bKey = (b.auto_label || b.zone_name || b.name || '').toString();
    return aKey.localeCompare(bKey);
  });
};

const GuidedLocationPicker = ({
  locations = [],
  guidedSearch,
  setGuidedSearch,
  onApplyLocation,
  reporterGps = null,
  onLocationsChanged,
  originIncidentId = null,
  deferCreate = false,
  className = '',
}) => {
  const isAdmin = useMemo(() => getIsAdmin(), []);
  const [guidedQuery, setGuidedQuery] = useState('');

  // Inline "Create Location" form state — mirrors the Locations & Zones module:
  // a node is (type, value, gps) created either as a new top-level hierarchy or
  // nested under the currently browsed parent.
  const [creating, setCreating] = useState(false);
  const [newLocType, setNewLocType] = useState('');
  const [newLocValue, setNewLocValue] = useState('');
  const [typeSuggestions, setTypeSuggestions] = useState([]);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  // Chips created in this session, so they show up immediately even before the
  // parent component refetches its locations list.
  const [localLocations, setLocalLocations] = useState([]);

  const safeLocations = useMemo(
    () => (Array.isArray(locations) ? locations : (locations?.locations || locations?.zones || [])),
    [locations]
  );

  const mergedLocations = useMemo(() => {
    if (!localLocations.length) return safeLocations;
    const byId = new Map(safeLocations.map((l) => [l.id, l]));
    localLocations.forEach((l) => {
      if (!byId.has(l.id)) byId.set(l.id, l);
    });
    return Array.from(byId.values());
  }, [safeLocations, localLocations]);

  const hierarchicalSearchResults = useMemo(() => {
    if (!guidedQuery.trim()) return [];
    return filterAndRankLocations(mergedLocations, guidedQuery, {
      allLocations: mergedLocations,
    });
  }, [mergedLocations, guidedQuery]);

  if (!guidedSearch?.active) return null;

  const locationIds = new Set(mergedLocations.map((l) => l.id));
  const childrenWithKids = new Set(mergedLocations.map((l) => l.parent_id).filter(Boolean));

  const currentChildren = sortChildrenByHierarchy(
    mergedLocations.filter((l) => {
      if (guidedSearch.selectedParentId === null) {
        return !l.parent_id || !locationIds.has(l.parent_id);
      }
      return l.parent_id === guidedSearch.selectedParentId;
    })
  );

  const buildLocationMatch = (loc) => buildFullPath(loc, mergedLocations);

  const closePicker = () => {
    setGuidedQuery('');
    resetCreateForm();
    setGuidedSearch({ active: false, selectedParentId: null, breadcrumb: [] });
  };

  const parentCrumb = guidedSearch.breadcrumb.length > 0
    ? guidedSearch.breadcrumb[guidedSearch.breadcrumb.length - 1]
    : null;

  const validGps = reporterGps && reporterGps.lat != null && reporterGps.lng != null
    ? { lat: Number(reporterGps.lat), lng: Number(reporterGps.lng) }
    : null;

  const drillInto = (loc) => {
    resetCreateForm();
    setGuidedSearch((prev) => ({
      ...prev,
      selectedParentId: loc.id,
      breadcrumb: [...prev.breadcrumb, {
        id: loc.id,
        type: loc.zone_type || '',
        value: getLocName(loc),
      }],
    }));
  };

  function resetCreateForm() {
    setCreating(false);
    setNewLocType('');
    setNewLocValue('');
    setTypeDropdownOpen(false);
    setCreateError('');
  }

  const openCreateForm = async () => {
    setCreating(true);
    setCreateError('');
    if (typeSuggestions.length === 0) {
      try {
        const res = await CommanServices.locationssuggestionszonetypes();
        if (res?.status && Array.isArray(res.data)) setTypeSuggestions(res.data);
      } catch {
        // suggestions are a convenience — the free-text input still works
      }
    }
  };

  // Append a new child node inside the hierarchy tree returned by
  // GET /api/locations/hierarchy/{id} (root children live in `details`,
  // deeper children in `subdetails`) — same shape Locations & Zones submits.
  const appendChildInHierarchy = (root, parentId, child) => {
    if (root.id === parentId) {
      root.details = [...(root.details || []), child];
      return true;
    }
    const walk = (nodes = []) => {
      for (const node of nodes) {
        if (node.id === parentId) {
          node.subdetails = [...(node.subdetails || []), child];
          return true;
        }
        if (walk(node.subdetails || [])) return true;
      }
      return false;
    };
    return walk(root.details || []);
  };

  const handleCreateLocation = async () => {
    const type = newLocType.trim();
    const value = newLocValue.trim();
    if (!type || !value) {
      setCreateError('Location type and location value are both required.');
      return;
    }
    if (deferCreate) {
      onApplyLocation?.({
        new_location: {
          location_type: type,
          location_value: value,
          parent_id: guidedSearch.selectedParentId || null,
        },
        location_match: parentCrumb ? `${parentCrumb.value}${PATH_SEP}${type}: ${value}` : `${type}: ${value}`,
      });
      resetCreateForm();
      return;
    }
    setSaving(true);
    setCreateError('');
    try {
      // Locations created live from an incident are tagged so the modules can
      // show a "Created during incident" review chip (see IncidentOriginChip).
      const originNote = originIncidentId
        ? `Created from incident ${originIncidentId} during active incident`
        : '';
      let res;
      if (!guidedSearch.selectedParentId) {
        // Top-level parent location → new hierarchy root (same payload as
        // the Locations & Zones module create flow).
        const fd = new FormData();
        fd.append('location_hierarchy', JSON.stringify({
          location_type: type,
          location_value: value,
          details: [],
          gps: validGps,
          departments: [],
          status: 'normal',
          description: originNote,
        }));
        fd.append('description', originNote);
        res = await CommanServices.setlocationshierarchy(fd);
      } else {
        // Nested location → load the parent's full hierarchy, append the new
        // node under the browsed parent, and save the hierarchy back.
        const parentId = guidedSearch.selectedParentId;
        const hierRes = await CommanServices.locationshierarchyByid(parentId);
        const hierarchy = hierRes?.data?.location_hierarchy;
        if (!hierRes?.status || !hierarchy) {
          throw new Error(hierRes?.message || 'Could not load the parent location hierarchy.');
        }
        const child = {
          type,
          value,
          gps: validGps,
          departments: [],
          subdetails: [],
          isEditing: false,
          description: originNote,
        };
        if (!appendChildInHierarchy(hierarchy, parentId, child)) {
          throw new Error('Could not find the parent location in its hierarchy.');
        }
        const fd = new FormData();
        fd.append('location_hierarchy', JSON.stringify(hierarchy));
        fd.append('description', hierRes?.data?.description || '');
        res = await CommanServices.updatelocationshierarchy(fd, parentId);
      }

      if (res?.status) {
        const chips = Array.isArray(res.data) ? res.data : [];
        if (chips.length > 0) {
          setLocalLocations((prev) => {
            const byId = new Map(prev.map((l) => [l.id, l]));
            chips.forEach((c) => {
              byId.set(c.id, {
                id: c.id,
                parent_id: c.parent_id || null,
                zone_type: c.type,
                zone_name: c.auto_label || `${c.type}: ${c.value}`,
              });
            });
            return Array.from(byId.values());
          });
        }
        resetCreateForm();
        onLocationsChanged?.();
      } else {
        setCreateError(res?.message || 'Failed to create the location.');
      }
    } catch (err) {
      setCreateError(err?.message || 'Failed to create the location.');
    } finally {
      setSaving(false);
    }
  };

  const filteredTypeSuggestions = typeSuggestions.filter((t) =>
    t.toLowerCase().includes(newLocType.trim().toLowerCase())
  );

  return (
    <div className={`mt-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl shadow-inner animate-in slide-in-from-bottom-2 duration-300 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
          <Sparkles className="h-3 w-3 text-violet-500" />
          Guided Location Search
        </h3>
        <button
          type="button"
          onClick={closePicker}
          className="p-1 hover:bg-slate-200 rounded-md text-slate-400 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-1 flex-wrap px-1">
          <button
            type="button"
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
              guidedSearch.breadcrumb.length === 0
                ? 'bg-violet-600 text-white shadow-sm'
                : 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer'
            }`}
            onClick={() => {
              if (guidedSearch.breadcrumb.length > 0) {
                resetCreateForm();
                setGuidedSearch((prev) => ({ ...prev, selectedParentId: null, breadcrumb: [] }));
              }
            }}
          >
            All Locations
          </button>
          {guidedSearch.breadcrumb.map((crumb, idx) => (
            <React.Fragment key={crumb.id}>
              <span className="text-[9px] text-slate-400">{PATH_SEP.trim()}</span>
              <button
                type="button"
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                  idx === guidedSearch.breadcrumb.length - 1
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer'
                }`}
                onClick={() => {
                  if (idx < guidedSearch.breadcrumb.length - 1) {
                    resetCreateForm();
                    setGuidedSearch((prev) => ({
                      ...prev,
                      selectedParentId: crumb.id,
                      breadcrumb: prev.breadcrumb.slice(0, idx + 1),
                    }));
                  }
                }}
              >
                {crumb.value}
              </button>
            </React.Fragment>
          ))}
        </div>

        {guidedSearch.breadcrumb.length > 0 && (
          <button
            type="button"
            className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-1"
            onClick={() => {
              const newBreadcrumb = guidedSearch.breadcrumb.slice(0, -1);
              resetCreateForm();
              setGuidedSearch((prev) => ({
                ...prev,
                selectedParentId: newBreadcrumb.length > 0 ? newBreadcrumb[newBreadcrumb.length - 1].id : null,
                breadcrumb: newBreadcrumb,
              }));
            }}
          >
            ← Back to {guidedSearch.breadcrumb.length > 1
              ? guidedSearch.breadcrumb[guidedSearch.breadcrumb.length - 2].value
              : 'All Locations'}
          </button>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={guidedQuery}
            onChange={(e) => setGuidedQuery(e.target.value)}
            placeholder="Search all locations..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
        </div>

        {isAdmin && !creating && (
          <button
            type="button"
            onClick={openCreateForm}
            className="w-full text-[11px] font-bold text-violet-600 hover:text-violet-700 py-2 border border-dashed border-violet-300 rounded-xl hover:bg-violet-50 transition-colors flex items-center justify-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            {parentCrumb ? `Create Location inside "${parentCrumb.value}"` : 'Create Top-Level Location'}
          </button>
        )}

        {isAdmin && creating && (
          <div className="p-3 bg-white border border-violet-200 rounded-xl shadow-sm space-y-3 animate-in slide-in-from-top-1 duration-200">
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] font-bold text-violet-600 uppercase tracking-widest flex items-center gap-1.5">
                <Plus className="h-3 w-3" />
                {parentCrumb ? (
                  <span>New location inside <span className="normal-case">"{parentCrumb.value}"</span></span>
                ) : (
                  'New top-level location'
                )}
              </h4>
              <button
                type="button"
                onClick={resetCreateForm}
                className="p-1 hover:bg-slate-100 rounded-md text-slate-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            <div className="relative">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Location Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newLocType}
                onChange={(e) => {
                  setNewLocType(e.target.value);
                  setTypeDropdownOpen(true);
                }}
                onFocus={() => setTypeDropdownOpen(true)}
                onBlur={() => setTimeout(() => setTypeDropdownOpen(false), 150)}
                placeholder="Type or select location type..."
                data-testid="guided-create-type"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              {typeDropdownOpen && filteredTypeSuggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-36 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                  {filteredTypeSuggestions.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setNewLocType(t);
                        setTypeDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-violet-50"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Location Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newLocValue}
                onChange={(e) => setNewLocValue(e.target.value)}
                placeholder="e.g. Main Hospital, 3, 101..."
                data-testid="guided-create-value"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            <div className={`flex items-start gap-2 p-2 rounded-lg border text-[10px] leading-relaxed ${
              validGps
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-slate-50 border-slate-200 text-slate-500'
            }`}>
              <Crosshair className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              {validGps ? (
                <span>
                  <span className="font-bold">{deferCreate ? 'Reporter GPS evidence' : 'GPS Location'}</span>
                  {deferCreate ? ' — available for review on the next step: ' : ' — auto-captured from the reporter device: '}
                  <span className="font-mono">{validGps.lat.toFixed(6)}, {validGps.lng.toFixed(6)}</span>
                </span>
              ) : (
                <span>
                  No reporter GPS available — the new location will inherit GPS from its parent (if any).
                </span>
              )}
            </div>

            {createError && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="guided-create-error">{createError}</p>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetCreateForm}
                disabled={saving}
                className="flex-1 text-[11px] font-bold text-slate-500 hover:text-slate-700 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateLocation}
                disabled={saving || !newLocType.trim() || !newLocValue.trim()}
                data-testid="guided-create-submit"
                className="flex-1 text-[11px] font-bold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5"
              >
                {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                {saving ? 'Creating…' : (deferCreate ? 'Continue' : 'Create Location')}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2">
          {guidedQuery.trim() ? (
            hierarchicalSearchResults.length > 0 ? (
              hierarchicalSearchResults.map((loc) => {
                const fullPath = buildLocationFullPath(loc, mergedLocations);
                return (
                  <button
                    key={loc.id}
                    type="button"
                    className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-all text-left group"
                    onClick={() => {
                      onApplyLocation({
                        location_id: loc.id,
                        location_match: buildLocationMatch(loc),
                      });
                      setGuidedQuery('');
                    }}
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-slate-700 block truncate">
                        {getLocName(loc)}
                      </span>
                      {fullPath && fullPath !== getLocName(loc) && (
                        <span className="text-[10px] text-violet-600 block truncate mt-0.5">
                          {fullPath}
                        </span>
                      )}
                    </div>
                    <span className="text-[8px] text-violet-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                      Apply
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="text-center py-4 text-sm text-slate-400">
                No location found
              </div>
            )
          ) : currentChildren.map((loc) => {
            const hasChildren = childrenWithKids.has(loc.id);

            return (
              <button
                key={loc.id}
                type="button"
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-all text-left group"
                onClick={() => {
                  if (hasChildren) {
                    drillInto(loc);
                  } else {
                    onApplyLocation({
                      location_id: loc.id,
                      location_match: buildLocationMatch(loc),
                    });
                  }
                }}
              >
                <span className="text-sm font-bold text-slate-700 flex items-center gap-1">
                  {getLocName(loc)}
                  {hasChildren && (
                    <ChevronDown className="h-3 w-3 text-slate-400 group-hover:text-violet-500" />
                  )}
                </span>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {!hasChildren && (
                    <span className="text-[8px] text-violet-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                      Apply
                    </span>
                  )}
                  {!hasChildren && isAdmin && (
                    <span
                      role="button"
                      tabIndex={0}
                      title="Open to add a sub-location inside"
                      onClick={(e) => {
                        e.stopPropagation();
                        drillInto(loc);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          drillInto(loc);
                        }
                      }}
                      className="p-1 rounded-md text-slate-300 hover:text-violet-600 hover:bg-violet-100 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <ChevronRight className={`h-4 w-4 transition-colors ${
                    hasChildren
                      ? 'text-slate-300 group-hover:text-violet-500'
                      : 'text-violet-400 group-hover:text-violet-600'
                  }`} />
                </div>
              </button>
            );
          })}

          {!guidedQuery.trim() && currentChildren.length === 0 && (
            <div className="text-center py-4 text-sm text-slate-400">
              No locations found at this level.
            </div>
          )}

          <button
            type="button"
            className="mt-2 text-[11px] font-bold text-amber-600 hover:text-amber-700 text-center py-2 border-t border-slate-200/50 flex flex-col items-center gap-1"
            onClick={() => onApplyLocation({
              location_id: 'unknown',
              location_match: 'Unknown (Needs Mapping)',
              human_summary: 'Location manually marked as Unknown (Needs Mapping) by reporter.',
            })}
          >
            <span>My location isn&apos;t listed (Mark as Unknown)</span>
            <span className="text-[9px] font-normal text-slate-400 italic">Admin will manually map this later</span>
          </button>

          {guidedSearch.selectedParentId && (() => {
            const parentLoc = mergedLocations.find((l) => l.id === guidedSearch.selectedParentId);
            if (!parentLoc) return null;
            return (
              <button
                type="button"
                className="mt-1 text-[11px] font-bold text-violet-600 hover:text-violet-700 text-center py-2 border-t border-slate-200/50 flex items-center justify-center gap-1"
                onClick={() => onApplyLocation({
                  location_id: parentLoc.id,
                  location_match: buildLocationMatch(parentLoc),
                })}
              >
                <MapPin className="h-3 w-3" />
                Use this location: {getLocName(parentLoc)}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default GuidedLocationPicker;
